// 담당: 현정
// 월별 추이 차트 공용 — 기간 필터(3/6/12/전체) + hover 툴팁.
//
// 매출 집계가 전체 기간으로 열리면서 월 수가 71개까지 늘어, 축 라벨이 겹치고 막대가 실처럼 얇아졌다.
// 데이터를 버리는 게 아니라 **보는 창을 고르게** 한다 — 기본 12개월, '전체'로 언제든 전 구간.
import { useState, type ReactNode } from 'react'

export type TrendRange = 3 | 6 | 12 | 0 // 0 = 전체

const OPTIONS: { value: TrendRange; label: string }[] = [
  { value: 3, label: '3개월' },
  { value: 6, label: '6개월' },
  { value: 12, label: '1년' },
  { value: 0, label: '전체' },
]

/** 기간 상태 + 슬라이스. 데이터가 창보다 짧으면 그 창 버튼은 숨긴다(빈 선택지 금지). */
export function useTrendRange<T>(rows: T[], initial: TrendRange = 12) {
  const [range, setRange] = useState<TrendRange>(initial)
  const total = rows.length
  const effective: TrendRange = range !== 0 && total <= range ? 0 : range
  const sliced = effective === 0 ? rows : rows.slice(-effective)
  // 전체보다 짧은 창만 노출 + '전체'는 항상. 12개월치도 없는데 "1년" 버튼을 두면 눌러도 그대로다.
  const options = OPTIONS.filter((o) => o.value === 0 || o.value < total)
  return { range: effective, setRange, rows: sliced, options, total }
}

export function TrendRangeTabs({
  range,
  onChange,
  options,
}: {
  range: TrendRange
  onChange: (r: TrendRange) => void
  options: { value: TrendRange; label: string }[]
}) {
  if (options.length <= 1) return null
  return (
    <div className="flex items-center border border-line-soft">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={[
            'px-2.5 py-1 font-text text-[11px] transition-colors',
            o.value === range ? 'bg-ink-50 font-medium text-ink-800' : 'text-fg-3 hover:text-fg-2',
          ].join(' ')}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** 차트 위에 띄우는 hover 툴팁. x 는 0~100(%) — 컨테이너 폭 기준이라 뷰박스 좌표계와 무관하다. */
export function ChartTooltip({
  xPct,
  title,
  rows,
}: {
  xPct: number
  title: string
  rows: { label: string; value: string; color?: string }[]
}) {
  // 좌우 끝에서 잘리지 않게 변환을 접는다(가운데는 -50%, 양끝은 0/-100%).
  const clamped = Math.max(0, Math.min(100, xPct))
  const shift = clamped < 12 ? '0' : clamped > 88 ? '-100%' : '-50%'
  return (
    <div
      className="pointer-events-none absolute top-0 z-10 whitespace-nowrap border border-line-soft bg-surface-base px-2.5 py-1.5 shadow-lg"
      style={{ left: `${clamped}%`, transform: `translateX(${shift})` }}
    >
      <div className="font-text text-[11px] font-medium text-ink-800">{title}</div>
      {rows.map((r) => (
        <div key={r.label} className="mt-0.5 flex items-center gap-1.5 font-text text-[11px]">
          {r.color && (
            <span className="inline-block h-[6px] w-[6px] flex-none" style={{ background: r.color }} aria-hidden />
          )}
          <span className="text-fg-3">{r.label}</span>
          <span className="ml-auto pl-2 font-medium tabular-nums text-fg-1">{r.value}</span>
        </div>
      ))}
    </div>
  )
}

/** hover 감지용 투명 밴드 — SVG 위에 겹쳐 각 데이터 포인트의 폭만큼 영역을 만든다.
 *  SVG 안에서 처리하면 좌표 변환이 뷰박스에 묶여 반응형에서 어긋난다. */
export function HoverBands({
  count,
  onEnter,
  onLeave,
}: {
  count: number
  onEnter: (i: number) => void
  onLeave: () => void
}) {
  return (
    <div className="absolute inset-0 flex" onMouseLeave={onLeave}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="h-full flex-1" onMouseEnter={() => onEnter(i)} />
      ))}
    </div>
  )
}

/** 차트 + 툴팁을 함께 담는 relative 래퍼. */
export function ChartFrame({ children }: { children: ReactNode }) {
  return <div className="relative">{children}</div>
}
